// Vehicle management page — edit info, manage photos, view rental history

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vehicleApi } from '../../lib/api';
import { compressImageAsFile } from '../../components/shared/ImageCompressor';
import toast from 'react-hot-toast';

type Tab = 'info' | 'photos' | 'history';

export default function VehicleManagePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<any>(null);
  const [rentals, setRentals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('info');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!id) return;
    Promise.all([
      vehicleApi.getById(id),
      vehicleApi.getRentals(id),
    ]).then(([v, r]: any[]) => {
      setVehicle(v);
      setRentals(r.rentals || []);
      setForm({
        license_plate: v.license_plate || '',
        vin: v.vin || '',
        color: v.color || '',
        mileage: v.mileage || '',
        daily_rate: v.daily_rate || '',
        deposit_required: v.deposit_required || 0,
        location_city: v.location_city || '서울',
        location_district: v.location_district || '',
        location_detail: v.location_detail || '',
        description: v.description || '',
        features: v.features || '',
        min_rental_days: v.min_rental_days || 1,
        max_rental_days: v.max_rental_days || '',
        mileage_limit_per_day: v.mileage_limit_per_day || '',
        can_drive_public_road: !!v.can_drive_public_road,
        carrier_available: !!v.carrier_available,
      });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const update = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const data: any = {};
      for (const [k, v] of Object.entries(form)) {
        if (v !== '' && v !== undefined) data[k] = v;
      }
      await vehicleApi.update(id, data);
      toast.success('차량 정보가 업데이트되었습니다');
    } catch (err: any) {
      toast.error(err.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !id) return;

    for (const file of Array.from(files)) {
      try {
        const compressed = await compressImageAsFile(file);
        const formData = new FormData();
        formData.append('file', compressed);
        await vehicleApi.uploadPhoto(id, formData);
      } catch (err: any) {
        toast.error(`사진 업로드 실패: ${err.message}`);
      }
    }
    toast.success(`${files.length}장 업로드 완료`);
    // Reload vehicle data
    const v: any = await vehicleApi.getById(id);
    setVehicle(v);
  };

  const handlePhotoDelete = async (photoId: string) => {
    if (!id) return;
    try {
      await vehicleApi.deletePhoto(id, photoId);
      toast.success('사진이 삭제되었습니다');
      const v: any = await vehicleApi.getById(id);
      setVehicle(v);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';
  const formatDate = (d: string) => new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });

  const STATUS_LABELS: Record<string, string> = {
    requested: '검토 중', host_approved: '호스트 승인', payment_pending: '견적 확정',
    payment_authorized: '카드 등록 완료', confirmed: '예약 확정', in_progress: '대여 중',
    completed: '반납 완료', payment_captured: '정산 완료', settled: '종료',
    cancelled: '취소됨', rejected_by_host: '거절', rejected_by_admin: '거절',
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }
  if (!vehicle) {
    return <div className="text-center py-20 text-gray-400">차량을 찾을 수 없습니다</div>;
  }

  const input = (label: string, key: string, type: string = 'text', placeholder?: string) => (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        className="input-field text-sm"
        value={form[key] ?? ''}
        onChange={e => update(key, type === 'number' ? parseInt(e.target.value) || '' : e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <button onClick={() => navigate(-1)} className="text-sm text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500 rounded-lg">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
        뒤로
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{vehicle.make} {vehicle.model}</h1>
          <p className="text-sm text-gray-500">{vehicle.year}년식 · {vehicle.vehicle_type} · {vehicle.location_city}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
        {[
          { key: 'info', label: '정보 수정' },
          { key: 'photos', label: `사진 관리 (${vehicle.photos?.length || 0})` },
          { key: 'history', label: '대여 이력' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key as Tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Info Tab */}
      {tab === 'info' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {input('차량번호', 'license_plate')}
            {input('차대번호(VIN)', 'vin')}
            {input('색상', 'color')}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {input('주행거리(km)', 'mileage', 'number')}
            {input('일일 대여료(원)', 'daily_rate', 'number')}
            {input('보증금(원)', 'deposit_required', 'number')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {input('최소 대여일수', 'min_rental_days', 'number')}
            {input('최대 대여일수', 'max_rental_days', 'number')}
            {input('일일 주행제한(km)', 'mileage_limit_per_day', 'number')}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">지역</label>
              <select className="input-field text-sm" value={form.location_city} onChange={e => update('location_city', e.target.value)}>
                {['서울','경기','인천','부산','대구','광주','대전'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {input('구/군', 'location_district')}
            {input('상세 주소', 'location_detail', 'text', '게스트에게 일부만 노출')}
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.can_drive_public_road} onChange={e => update('can_drive_public_road', e.target.checked)} className="focus:outline-none focus:ring-2 focus:ring-brand-500 rounded" />
              일반도로 주행 가능
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.carrier_available} onChange={e => update('carrier_available', e.target.checked)} className="focus:outline-none focus:ring-2 focus:ring-brand-500 rounded" />
              캐리어 탁송 가능
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">차량 설명</label>
            <textarea className="input-field text-sm" rows={3} value={form.description} onChange={e => update('description', e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">특징 및 출연 작품</label>
            <textarea className="input-field text-sm" rows={2} value={form.features} onChange={e => update('features', e.target.value)} placeholder="영화 OOO 출연, 드라마 OOO 등" />
          </div>

          <button onClick={handleSave} disabled={saving} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl text-sm transition-colors duration-150 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500">
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      )}

      {/* Photos Tab */}
      {tab === 'photos' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">차량 사진</h3>
            <label className="cursor-pointer bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors duration-150 focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-500">
              + 사진 추가
              <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" />
            </label>
          </div>

          {(!vehicle.photos || vehicle.photos.length === 0) ? (
            <p className="text-sm text-gray-400 text-center py-8">등록된 사진이 없습니다</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {vehicle.photos.map((p: any) => (
                <div key={p.id} className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                  <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => handlePhotoDelete(p.id)}
                    className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  {p.is_primary === 1 && (
                    <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 bg-slate-900/70 text-white text-[10px] font-medium rounded-md">대표</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          {rentals.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">대여 이력이 없습니다</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {rentals.map((r: any) => (
                <div key={r.id} className="p-4 flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-slate-900">{r.guest_name}</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {formatDate(r.pickup_date)} ~ {formatDate(r.return_date)} ({r.rental_days}일)
                    </p>
                    <p className="text-gray-400 text-xs">ID: {r.id.slice(0, 8)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900">{formatPrice(r.total_amount)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      r.status === 'completed' || r.status === 'settled' ? 'bg-emerald-50 text-emerald-600' :
                      r.status === 'cancelled' || r.status.includes('rejected') ? 'bg-red-50 text-red-500' :
                      'bg-blue-50 text-blue-600'
                    }`}>
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
